import { Button as AnButton, Flex, Input, Tooltip, Typography } from 'antd';
import Checked from 'Assets/icons/checked-circle.svg';
import classNames from 'classnames';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from 'semantic-ui-react';
import { AI_CHECKER_GA_EVENT } from 'Containers/Teleconsult/Main/AINotes/constants';

import useDoctorEvaluate from '../../hooks/useDoctorEvaluate';
import useAiCheckerLogEvent from '../../hooks/useAiCheckerLogEvent';

import styles from './Feedback.less';

const ACTIONS = {
  THUMBS_UP: 'like',
  THUMBS_DOWN: 'unlike',
};

const Thanks = () => {
  return (
    <Flex vertical gap={8} justify="center" align="center">
      <img src={Checked} alt="Checked Icon" />
      <Typography.Title level={4} className={styles.title}>
        Thank you for your feedback!
      </Typography.Title>
    </Flex>
  );
};

interface FeedbackProps {
  id: string;
  consultId: string;
  score?: number | string;
  userFeedback?: string;
}

const Feedback = ({ id, consultId, score, userFeedback }: FeedbackProps) => {
  const [action, setAction] = useState('');
  const [text, setText] = useState('');

  const { isLoading, isSuccess, handleDoctorSendEvaluate } = useDoctorEvaluate();

  const { logAICheckerEvent } = useAiCheckerLogEvent(score);

  const handleSubmit = () => {
    handleDoctorSendEvaluate(id, {
      note: text,
      feedback: action,
    });
    logAICheckerEvent(AI_CHECKER_GA_EVENT.AI_CHECKER_FEEDBACK, {
      teleconsult_id: consultId,
      content_type: action === ACTIONS.THUMBS_UP ? 'Looks good' : 'Needs improvement',
      content: text,
    });
  };

  const buttonClassName = currentAction =>
    classNames(styles.optionButton, {
      [styles.active]: currentAction === action,
    });

  const isDisabled = !text.trim() && action === ACTIONS.THUMBS_DOWN;
  const placeholderText =
    action === ACTIONS.THUMBS_DOWN
      ? 'Tell us what didn’t feel right - any detail helps.'
      : 'Tell us what you liked most!';

  return (
    <Flex className={styles.container} vertical gap={12} justify="center" align="center">
      {isSuccess || !!userFeedback ? (
        <Thanks />
      ) : (
        <>
          <div className={styles.label}>Help us out!</div>
          <Typography.Title level={4} className={styles.title}>
            How accurate was this evaluation?
          </Typography.Title>
          <Flex className={styles.feedbackOptions} gap={8}>
            <AnButton
              shape="circle"
              type="default"
              className={buttonClassName(ACTIONS.THUMBS_UP)}
              onClick={() => setAction(ACTIONS.THUMBS_UP)}>
              <Tooltip title="Looks good" placement="bottom">
                <ThumbsUp
                  size={12}
                  fill={action === ACTIONS.THUMBS_UP ? '#0984E3' : 'none'}
                  strokeWidth={action === ACTIONS.THUMBS_UP ? 0 : 1}
                />
              </Tooltip>
            </AnButton>
            <AnButton
              type="default"
              shape="circle"
              className={buttonClassName(ACTIONS.THUMBS_DOWN)}
              onClick={() => setAction(ACTIONS.THUMBS_DOWN)}>
              <Tooltip title="Needs improvement" placement="bottom">
                <ThumbsDown
                  size={12}
                  fill={action === ACTIONS.THUMBS_DOWN ? '#0984E3' : 'none'}
                  strokeWidth={action === ACTIONS.THUMBS_DOWN ? 0 : 1}
                />
              </Tooltip>
            </AnButton>
          </Flex>
          {!!action && (
            <>
              <Input.TextArea
                className={styles.textArea}
                placeholder={placeholderText}
                rows={4}
                onChange={e => {
                  setText(e.target.value);
                }}
              />
              <Button
                primary
                className={styles.submitButton}
                disabled={isDisabled}
                loading={isLoading}
                onClick={handleSubmit}>
                Submit Feedback
              </Button>
            </>
          )}
        </>
      )}
    </Flex>
  );
};

export default Feedback;
